/**
 * Mesh: the organisation's private network. The data column (Layout.tsx): a header, a How it works,
 * the numbers, the administrators' tabs, then on Machines the gateway, the machines table, the chosen
 * machine's facts and peers, its flow log and the relays, each a list group of rows.
 */

import type { MeshGatewayView, MeshNode, MeshNodeDetail, MeshPeer, MeshPeerStats, MeshRelayInstanceView, MeshRelaysResponse } from '@aioe/shared';
import { BroadcastSignal, NodesRight, Pencil, Server, Shield } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { Tooltip } from '@heroui/react';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { FlowLog, MeshLogs } from '../components/meshLogs.tsx';
import { MeshMachines } from '../components/meshMachines.tsx';
import { MeshPolicyEditor, portsInWords, type PolicySection } from '../components/meshPolicy.tsx';
import { MeshNames, MeshServices } from '../components/meshServices.tsx';
import { MeshDomains, MeshJoinKeys, PolicyFiles } from '../components/meshSettings.tsx';
import { MeshWebhooks } from '../components/meshWebhooks.tsx';
import { bytesInWords, offTheMeshInWords, postureInWords, relayAllowanceInWords } from '../components/meshWords.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

/** How a node's traffic travels, in words: the endpoints stop mattering once it is relayed. */

const DAY = 86_400_000;

/** The key's expiry in words: nothing when it never expires. */
function keyExpiry(n: Pick<MeshNode, 'keyExpiresAt'>, now = Date.now()): { text: string; expired: boolean } | undefined {
  if (!n.keyExpiresAt) return undefined;
  const left = Date.parse(n.keyExpiresAt) - now;
  if (left <= 0) return { text: 'key expired', expired: true };
  if (left < DAY) return { text: 'key expires today', expired: false };
  const days = Math.ceil(left / DAY);
  return { text: `key expires in ${days} ${days === 1 ? 'day' : 'days'}`, expired: false };
}

/** What the peer may reach on this node, and this node on it, in words. */
function peerPorts(p: MeshPeer, self: string): string {
  const theirs = p.allowedPorts;
  const mine = p.reachablePorts;
  const reachesHere = !(theirs && theirs.length === 1 && theirs[0] === '0');
  const reachesThere = !(mine && mine.length === 1 && mine[0] === '0');
  const parts = [reachesHere ? `reaches ${self} on ${portsInWords(theirs)}` : `cannot reach ${self}`];
  if (reachesThere && (mine?.length ?? 0) > 0) parts.push(`is reachable on ${portsInWords(mine)}`);
  return parts.join('; ');
}

/** A machine's own node (a workbench or a headless node), whose device reports posture; not a phone, a gateway or a relay. */
const hasDevicePosture = (n: MeshNode) => n.kind === 'workbench' || n.kind === 'node';

/** Where the policy forces the relay, the path in words. */
const FORCED_LABEL = { 'relay-https': 'via relay (HTTPS)', relay: 'via relay' } as const;
type Forced = keyof typeof FORCED_LABEL;

/** A relay instance as people count it: relay 1, relay 2. */
const relayName = (instance: number) => `relay ${instance + 1}`;

/** The relay a route id belongs to, in words ("relay 2"); the route id itself when the instances are not known here. */
function relayOf(relays: MeshRelaysResponse | undefined, routeId: string | undefined): string | undefined {
  if (!routeId) return undefined;
  const at = relays?.instances.find((i) => i.routes.some((r) => r.id === routeId));
  return at ? relayName(at.instance) : routeId;
}

/** How one peer's traffic travels and how much, in words. Where the policy forces the relay, relayed is the chosen path, not a fallback. */
function PeerTraffic({ stats, handshake, relay, forced }: { stats?: MeshPeerStats; handshake?: string; relay?: string; forced?: Forced }) {
  const relayed = stats?.via === 'relay';
  const label = relayed ? (forced ? `${FORCED_LABEL[forced][0]!.toUpperCase()}${FORCED_LABEL[forced].slice(1)}` : 'Relayed') + (relay ? ` through ${relay}` : '') : 'Direct';
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted">
        {stats?.via ? <StatusMark state={relayed && !forced ? 'medium' : 'connected'} label={label} /> : <span>Path not reported</span>}
        {(stats?.rxBytes !== undefined || stats?.txBytes !== undefined) && (
          <span className="tabular-nums">
            {bytesInWords(stats?.rxBytes)} in, {bytesInWords(stats?.txBytes)} out
          </span>
        )}
        <span>{handshake ? `handshake ${timeAgo(handshake)}` : 'no handshake yet'}</span>
        {stats?.refused ? <span className="tabular-nums">{stats.refused} refused</span> : null}
      </div>
      {relayed && !forced && <p className="text-xs text-warning">No direct path between these two: their traffic goes through the relay, which is slower. A firewall or a strict NAT on one side is the usual cause.</p>}
    </>
  );
}

/** Requested tags the platform has not given the node yet. */
const waiting = (n: MeshNode) => (n.requestedTags ?? []).filter((t) => !n.tags.includes(t));

type Tab = 'machines' | 'services' | 'names' | 'access' | 'logs' | 'settings';
const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ['machines', 'Machines'],
  ['services', 'Services'],
  ['names', 'Names'],
  ['access', 'Access controls'],
  ['logs', 'Logs'],
  ['settings', 'Settings'],
];
const SUBTABS: Partial<Record<Tab, ReadonlyArray<readonly [string, string]>>> = {
  access: [
    ['rules', 'General access rules'],
    ['remote', 'Remote control'],
    ['approvers', 'Auto approvers'],
    ['tests', 'Tests'],
    ['definitions', 'Definitions'],
    ['json', 'JSON'],
  ],
  settings: [
    ['device', 'Device management'],
    ['files', 'Policy file management'],
    ['domains', 'Domains'],
    ['webhooks', 'Webhooks'],
    ['keys', 'Keys'],
  ],
};

const SUBTITLE = 'The private network between your workbenches, phones and gateways; each machine sees only the peers the policy allows.';

/** The mesh: Enterprise only. Mojo Up AI Cloud carries no mesh on any plan (JP, 21 Oct 2026), and its routes answer 404 there. */
export function MeshPage() {
  return <MeshView />;
}

function MeshView() {
  const { discovery } = useAuth();
  // The Machines list pages itself; the headline numbers come with one machine, and the other tabs (names, domains) read the whole list only when shown.
  const [rev, setRev] = useState(0);
  const summary = useLoad(() => routes.meshNodesPage({ limit: 1 }), [rev], 15_000);

  const policy = useLoad(() => routes.meshPolicy().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  const [selected, setSelected] = useState<string>();
  const detail = useLoad(() => (selected ? routes.meshNode(selected) : Promise.resolve(undefined)), [selected]);
  const counted = summary.data?.extra;
  // Administrators read the policy, so they see where the relay is forced on every row; anyone else sees what each node reports.
  const mode = policy.data?.transport?.mode;
  const forced: Forced | undefined = mode === 'relay-https' || mode === 'relay' ? mode : undefined;
  // Only administrators can read the policy, so it doubles as the check for admin-only actions.
  const isAdmin = policy.data !== undefined;
  const [accepting, setAccepting] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab: Tab = isAdmin && TABS.some(([id]) => id === params.get('tab')) ? (params.get('tab') as Tab) : 'machines';
  // A link to another tab waits for the policy (which says whether the person may see it) rather than flashing the machines first.
  const nodes = useLoad(() => (tab !== 'machines' ? routes.meshNodes() : Promise.resolve([] as MeshNode[])), [rev, tab], 15_000);
  const reloadNodes = () => {
    setRev((r) => r + 1);
    nodes.reload();
  };
  const rows = nodes.data ?? [];
  const waitingForTab = params.has('tab') && !isAdmin && policy.loading;
  const sub = SUBTABS[tab];
  const part = sub?.some(([id]) => id === params.get('part')) ? params.get('part')! : (sub?.[0]?.[0] ?? '');
  const setTab = (t: Tab) => setParams(t === 'machines' ? {} : { tab: t }, { replace: true });
  const setPart = (p: string) => setParams({ tab, part: p }, { replace: true });
  const editorSection: PolicySection | undefined = tab === 'access' ? (part as PolicySection) : tab === 'settings' && part === 'device' ? 'device' : undefined;
  // What the Machines table shows beside the nodes: owners by name, each device's OS, the newest mesh agent the portal holds.
  const defs = useLoad(() => (isAdmin ? routes.meshDefinitions().catch(() => undefined) : Promise.resolve(undefined)), [isAdmin]);
  const devices = useLoad(() => (isAdmin ? routes.devices().catch(() => undefined) : Promise.resolve(undefined)), [isAdmin]);
  const releases = useLoad(() => (isAdmin ? routes.releases().catch(() => undefined) : Promise.resolve(undefined)), [isAdmin]);
  const latestAgent = releases.data?.products.find((p) => p.product === 'mesh')?.latest;
  const [removing, setRemoving] = useState(false);
  // How remote control reaches this organisation: the platform's gateway, and the relay instances (administrators).
  const gateway = useLoad(() => (isAdmin ? routes.meshGateway() : Promise.resolve(undefined)), [isAdmin], 60_000);
  // Consecutive answers in which each instance missed packets: one bad minute is noise, several in a row are worth a word.
  const missStreaks = useRef(new Map<number, number>());
  const relays = useLoad(
    async () => {
      if (!isAdmin) return undefined;
      const r = await routes.meshRelays();
      for (const i of r.instances) missStreaks.current.set(i.instance, i.missesPerMinute ? (missStreaks.current.get(i.instance) ?? 0) + 1 : 0);
      return { ...r, persisting: new Set(r.instances.filter((i) => (missStreaks.current.get(i.instance) ?? 0) >= MISSES_PERSIST).map((i) => i.instance)) };
    },
    [isAdmin],
    30_000,
  );
  async function accept(n: MeshNode) {
    setAccepting(true);
    try {
      await routes.setMeshNodeTags(n.id, [...new Set([...n.tags, ...(n.requestedTags ?? [])])]);
      reloadNodes();
      detail.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAccepting(false);
    }
  }
  const tone = VIEW_TONES.mesh!;
  const node = detail.data?.node;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<NodesRight />}
        title="Mesh"
        subtitle={SUBTITLE}
        actions={
          <Button size="sm" variant="secondary" onPress={reloadNodes}>
            Refresh
          </Button>
        }
      />
      <Explainer id="mesh" tone={tone} icon={<NodesRight />} title="How the mesh works" summary="A private network between your machines; the access rules decide who reaches whom">
        <p>Every workbench, headless node and phone that joins gets an address on the organisation’s private network and a name under the mesh’s domain. Traffic between two machines is encrypted end to end and goes directly when it can, or through a relay when a firewall is in the way.</p>
        <p>A machine sees only the peers the access rules allow, on the ports they allow. Tags are what the rules pick machines by; a machine may ask for a tag, and it counts once an administrator gives it.</p>
        <p>Choose a machine to see who it can reach and how its traffic travels. Administrators edit the access rules, keys, names and webhooks in the other tabs.</p>
      </Explainer>
      <div className="art-stats">
        <Stat tone={tone} icon={<NodesRight />} label="Nodes" value={counted?.total} detail={counted ? `${counted.summary.online} online` : ''} />
        <Stat tone={tone} icon={<NodesRight />} label="Workbenches" value={counted?.summary.workbenches} detail={counted ? `${counted.summary.phones} phones, ${counted.summary.gateways} gateways` : ''} />
        <Stat tone={tone} icon={<Shield />} label="Private network" value={discovery?.capabilities.includes('mesh') ? 'On' : 'Off'} detail={discovery?.meshUrl?.replace(/^https?:\/\//, '') ?? ''} />
      </div>
      <ErrorNote error={nodes.error} />
      {isAdmin && (
        <div className="flex max-w-full flex-wrap items-center gap-2 overflow-x-auto">
          <Segment aria-label="Mesh" size="sm" selectedKey={tab} onSelectionChange={(k) => setTab(String(k) as Tab)} className="w-fit min-w-max">
            {TABS.map(([id, label]) => (
              <Segment.Item key={id} id={id}>
                {label}
              </Segment.Item>
            ))}
          </Segment>
          {sub && (
            <Segment aria-label={`${tab === 'access' ? 'Access controls' : 'Settings'}: which part`} size="sm" selectedKey={part} onSelectionChange={(k) => setPart(String(k))} className="w-fit min-w-max">
              {sub.map(([id, label]) => (
                <Segment.Item key={id} id={id}>
                  {label}
                </Segment.Item>
              ))}
            </Segment>
          )}
        </div>
      )}
      <PartBoundary key="machines" name="Machines" tone={tone}>
        {tab === 'machines' && !waitingForTab && (
          <>
            {gateway.data && <GatewayGroup gateway={gateway.data} />}
            {counted && counted.total === 0 ? (
              <Empty tone={tone} icon={<NodesRight />} title="No nodes yet" body="Run the mesh agent on a workbench (the desktop app starts it once enrolled), or connect the mobile app, and it appears here with its overlay address." />
            ) : (
              <MeshMachines
                rev={rev}
                ctx={{ names: new Map((defs.data?.people ?? []).flatMap((p) => (p.name ? [[p.id, p.name] as const] : []))), platforms: new Map((devices.data ?? []).map((d) => [d.deviceId, d.platform] as const)), ...(latestAgent ? { latestAgent } : {}) }}
                isAdmin={isAdmin}
                offAllowed={policy.data?.keyExpiryOffAllowed !== false}
                {...(selected ? { selected } : {})}
                onSelect={(id) => setSelected(id || undefined)}
                onChanged={() => {
                  reloadNodes();
                  detail.reload();
                }}
              />
            )}
            {selected && !node ? <ErrorNote error={detail.error} /> : null}
            {selected && node && detail.data ? (
              <>
                <ListGroup
                  title={node.name}
                  description="What the platform says about this machine."
                  actions={
                    <RowMenu
                      label={`More for ${node.name}`}
                      items={[
                        { id: 'toggle', label: node.disabled ? 'Enable' : 'Disable' },
                        { id: 'remove', label: 'Remove…', danger: true },
                      ]}
                      onAction={(k) =>
                        k === 'remove'
                          ? setRemoving(true)
                          : void routes.setMeshNodeDisabled(selected, !node.disabled).then(() => {
                              reloadNodes();
                              detail.reload();
                            })
                      }
                    />
                  }
                >
                  <NodeFacts
                    node={node}
                    posture={detail.data.posture}
                    isAdmin={isAdmin}
                    accepting={accepting}
                    onAccept={() => void accept(node)}
                    onRenamed={() => {
                      reloadNodes();
                      detail.reload();
                    }}
                  />
                </ListGroup>
                <ListGroup title={`Peers of ${node.name}`} count={detail.data.peers.length} description="Who it can reach, on which ports, and how the traffic travels.">
                  {detail.data.peers.map((p) => (
                    <ListRow
                      key={p.nodeId}
                      title={
                        <>
                          {p.name} <span className="text-xs font-normal text-muted">{p.kind}</span>
                        </>
                      }
                      description={
                        <>
                          <span className="mono block truncate" title={p.dnsName}>
                            {p.dnsName ? `${p.dnsName} · ` : ''}
                            {p.address}
                          </span>
                          <span className="block">{peerPorts(p, node.name)}</span>
                        </>
                      }
                    >
                      <PeerTraffic stats={detail.data!.peerStats?.[p.nodeId]} handshake={detail.data!.peerStats?.[p.nodeId]?.lastHandshake ?? detail.data!.handshakes[p.publicKey]} relay={relayOf(relays.data, detail.data!.peerStats?.[p.nodeId]?.relayId ?? detail.data!.relayId)} forced={detail.data!.transportForced ?? forced} />
                    </ListRow>
                  ))}
                  {detail.data.peers.length === 0 && <ListBlock className="text-sm text-muted">No peers: the policy lets this node reach nothing yet.</ListBlock>}
                  {detail.data.posture?.missingPeers.map((m) => <ListRow key={`missing-${m.nodeId}`} title={m.name} description={m.fails.map((f) => f.message).join('; ')} status={<StatusMark state="disabled" label="Left out by posture" />} />)}
                </ListGroup>
                <ConfirmDialog
                  isOpen={removing}
                  title={`Remove ${node.name} from the mesh?`}
                  confirmLabel="Remove"
                  danger
                  onCancel={() => setRemoving(false)}
                  onConfirm={async () => {
                    try {
                      await routes.deleteMeshNode(selected);
                      setSelected(undefined);
                      reloadNodes();
                    } catch (e) {
                      setError(problemText(e));
                    }
                    setRemoving(false);
                  }}
                >
                  <p>Its peers stop reaching it at once, and its key is retired for good: the machine comes back only with a new key, when its mesh agent joins again.</p>
                </ConfirmDialog>
              </>
            ) : null}
            {selected && detail.data && <FlowLog tone={tone} nodeId={selected} nodeName={detail.data.node.name} />}
            {relays.data && relays.data.instances.length > 0 && <RelaysGroup relays={relays.data} persisting={relays.data.persisting} />}
          </>
        )}
      </PartBoundary>
      {error && <ErrorNote error={error} />}
      <PartBoundary key="services" name="Services" tone={tone}>
        {isAdmin && tab === 'services' && <MeshServices tone={tone} />}
      </PartBoundary>
      <PartBoundary key="names" name="Names" tone={tone}>
        {isAdmin && tab === 'names' && <MeshNames tone={tone} nodes={rows} isAdmin={isAdmin} onChanged={reloadNodes} />}
      </PartBoundary>
      <PartBoundary key="logs" name="Logs" tone={tone}>
        {isAdmin && tab === 'logs' && <MeshLogs tone={tone} />}
      </PartBoundary>
      <PartBoundary key={`settings/files`} name="Policy files" tone={tone}>
        {isAdmin && tab === 'settings' && part === 'files' && <PolicyFiles tone={tone} onRestored={policy.reload} />}
      </PartBoundary>
      <PartBoundary key={`settings/domains`} name="Domains" tone={tone}>
        {isAdmin && tab === 'settings' && part === 'domains' && <MeshDomains tone={tone} nodes={rows} />}
      </PartBoundary>
      <PartBoundary key={`settings/keys`} name="Join keys" tone={tone}>
        {isAdmin && tab === 'settings' && part === 'keys' && <MeshJoinKeys tone={tone} />}
      </PartBoundary>
      <PartBoundary key={`settings/webhooks`} name="Webhooks" tone={tone}>
        {isAdmin && tab === 'settings' && part === 'webhooks' && <MeshWebhooks tone={tone} />}
      </PartBoundary>
      <PartBoundary key={`settings/device`} name="Device sign-in" tone={tone}>
        {isAdmin && tab === 'settings' && part === 'device' && <StepUpGroup />}
      </PartBoundary>
      <PartBoundary key={`policy/${tab}/${part}`} name="Access rules" tone={tone}>
        {policy.data && (
          <MeshPolicyEditor
            key={JSON.stringify(policy.data)}
            policy={policy.data}
            nodes={rows}
            tone={tone}
            {...(editorSection ? { section: editorSection } : {})}
            onPublished={() => {
              policy.reload();
              detail.reload();
            }}
          />
        )}
      </PartBoundary>
    </PageColumn>
  );
}

/** Answers in a row (30 seconds apart) with missed packets before the relay's card says so: about a minute and a half. */
const MISSES_PERSIST = 3;

/** The gateway's standing as a state and a word. */
function gatewayMark(g: MeshGatewayView): { state: Parameters<typeof StatusMark>[0]['state']; label: string } {
  if (!g.configured) return { state: 'disabled', label: 'No gateway' };
  if (g.served) return g.online ? { state: 'online', label: 'On your mesh' } : { state: 'working', label: 'Joining' };
  if (g.reason === 'capacity') return { state: 'medium', label: 'At its limit' };
  return { state: 'idle', label: 'Waiting for a machine' };
}

/** Whether the platform's gateway serves this organisation: remote control and the Screen view go over it first. */
function GatewayGroup({ gateway }: { gateway: MeshGatewayView }) {
  const mark = gatewayMark(gateway);
  return (
    <ListGroup title="Gateway" description="Carries remote control and the screen over the mesh.">
      <ListRow icon={<Server />} title={gateway.message} description={gateway.lastSeenAt ? `Last heard ${timeAgo(gateway.lastSeenAt)}` : undefined} status={<StatusMark state={mark.state} label={mark.label} />} />
    </ListGroup>
  );
}

const RELAY_HEALTH: Record<MeshRelayInstanceView['health'], { state: Parameters<typeof StatusMark>[0]['state']; label: string }> = {
  ok: { state: 'online', label: 'Answering' },
  unreachable: { state: 'error', label: 'Not answering' },
  unknown: { state: 'idle', label: 'Not checked' },
};

const count = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString());

/** Every relay instance: whether it answers, and what it carries for this organisation. */
function RelaysGroup({ relays, persisting }: { relays: MeshRelaysResponse; persisting: ReadonlySet<number> }) {
  const allowance = relayAllowanceInWords(relays.limits);
  return (
    <ListGroup title="Relays" count={relays.instances.length} description="Machines with no direct path reach each other through these. The numbers are your organisation's, for the last whole minute.">
      {relays.instances.map((r) => (
        <RelayRow key={r.instance} relay={r} missesPersist={persisting.has(r.instance)} />
      ))}
      {allowance && <ListBlock className="text-sm text-muted">On each relay, your organisation may {allowance}. Past that, a new connection waits a minute and traffic slows down.</ListBlock>}
    </ListGroup>
  );
}

function RelayRow({ relay: r, missesPersist }: { relay: MeshRelayInstanceView; missesPersist: boolean }) {
  const health = RELAY_HEALTH[r.health];
  const name = relayName(r.instance);
  return (
    <ListRow
      icon={<BroadcastSignal />}
      title={
        <>
          {name[0]!.toUpperCase() + name.slice(1)}
          {r.region ? <span className="ml-2 font-normal text-muted">{r.region}</span> : null}
        </>
      }
      description={
        <>
          <span className="tabular-nums">
            {count(r.sessions)} sessions · {r.bytesPerMinute === undefined ? '—' : bytesInWords(r.bytesPerMinute)} a minute · <span className={missesPersist ? 'text-warning' : ''}>{count(r.missesPerMinute)} missed packets</span>
          </span>
          <span className="block">
            {r.nodesReporting === 0 ? 'None of your machines relay through it now' : r.nodesReporting === 1 ? 'One of your machines relays through it' : `${r.nodesReporting} of your machines relay through it`}
            {r.version ? ` · version ${r.version}` : ''}
          </span>
          {missesPersist && <span className="block text-warning">Packets have gone missing here for more than a minute: a peer is offline, or the two ends of a pair reached different relays.</span>}
          {r.throttledPerMinute || r.refusedPerMinute ? (
            <span className="block text-warning">
              Over your allowance in the last minute:{' '}
              {[r.throttledPerMinute ? `${bytesInWords(r.throttledPerMinute)} held back` : '', r.refusedPerMinute ? `${r.refusedPerMinute.toLocaleString()} ${r.refusedPerMinute === 1 ? 'connection' : 'connections'} refused` : ''].filter(Boolean).join(', ')}.
            </span>
          ) : null}
          {r.detail && <span className="block">{r.detail}</span>}
        </>
      }
      status={<StatusMark state={health.state} label={health.label} />}
    />
  );
}

/** What the platform says about one node, a row each: its name, why it is off, its key, its posture, its tags and what it asks for. */
function NodeFacts({ node, posture, isAdmin, accepting, onAccept, onRenamed }: { node: MeshNode; posture?: MeshNodeDetail['posture']; isAdmin: boolean; accepting: boolean; onAccept: () => void; onRenamed: () => void }) {
  const expiry = keyExpiry(node);
  const asks = waiting(node);
  return (
    <>
      <ListRow title="Name" description={node.name} compactControl control={<NodeName key={node.name} node={node} onRenamed={onRenamed} />} />
      {node.disabled && <ListRow title="Status" description={offTheMeshInWords(node)} />}
      <ListRow title="Key" description={expiry ? (expiry.expired ? 'Key expired: the agent must rotate it to rejoin' : expiry.text[0]!.toUpperCase() + expiry.text.slice(1)) : 'Never expires'} />
      {hasDevicePosture(node) && <ListRow title="Posture" description={postureInWords(node.posture)[0]!.toUpperCase() + postureInWords(node.posture).slice(1)} />}
      {posture && posture.grants.length > 0 && (
        <ListRow
          title="Left out of"
          description={posture.grants.map((g) => (
            <span key={g.grant} className="block">
              <span className="text-foreground">{g.grant}</span>: {g.fails.map((f) => f.message).join('; ')}
            </span>
          ))}
        />
      )}
      <ListRow title="Tags" description={node.tags.length ? <span className="mono">{node.tags.join(', ')}</span> : 'None'} />
      {asks.length > 0 && (
        <ListRow
          title="Asks for"
          description={<span className="mono">{asks.join(', ')}</span>}
          compactControl
          control={
            isAdmin ? (
              <Button size="sm" variant="secondary" isPending={accepting} onPress={onAccept}>
                Accept
              </Button>
            ) : undefined
          }
        />
      )}
    </>
  );
}

/** Renames a node (administrators and operators; the API says so to anyone else) in a sheet. Its mesh name follows. */
function NodeName({ node, onRenamed }: { node: MeshNode; onRenamed: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(node.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const close = () => (setEditing(false), setName(node.name), setError(undefined));
  const ok = !!name.trim() && name.trim() !== node.name;
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.renameMeshNode(node.id, name.trim());
      setEditing(false);
      onRenamed();
    } catch (e) {
      const text = problemText(e);
      setError(text === 'You are not allowed to do that here.' ? 'Only administrators and operators rename a node.' : text);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Tooltip delay={300}>
        <Button size="sm" variant="ghost" isIconOnly className="text-muted" aria-label={`Rename ${node.name}`} onPress={() => setEditing(true)}>
          <Pencil className="size-3.5" />
        </Button>
        <Tooltip.Content>Rename</Tooltip.Content>
      </Tooltip>
      <FormSheet
        isOpen={editing}
        width="md"
        title={`Rename ${node.name}`}
        description="Its name on the mesh changes with it; peers find it by the new name."
        onClose={close}
        footer={
          <>
            <Button size="sm" variant="tertiary" onPress={close}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" isPending={busy} isDisabled={!ok} onPress={() => void save()}>
              Rename
            </Button>
          </>
        }
      >
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ok) void save();
          }}
        >
          <FormField label="Name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </FormField>
          <ErrorNote error={error} />
        </form>
      </FormSheet>
    </>
  );
}

const STEP_UP = { '0': 'Any sign-in', '15': 'Within 15 minutes', '30': 'Within 30 minutes', '60': 'Within an hour', '240': 'Within 4 hours', '480': 'Within 8 hours' } as const;

/** How recent a sign-in must be to take control of a workbench, attach to a host or add a repository to a node. */
function StepUpGroup() {
  const org = useLoad(() => routes.organisationProfile(), []);
  const [error, setError] = useState<string>();
  const minutes = String(org.data?.stepUpMinutes ?? 60);
  const options: Record<string, string> = minutes in STEP_UP ? { ...STEP_UP } : { ...STEP_UP, [minutes]: `Within ${minutes} minutes` };
  return (
    <>
      <ListGroup title="Sign-in for remote control">
        <ListRow
          title="How recent a sign-in must be"
          description="To take control of a workbench's screen, attach to a host or add a repository to a node. Older, and the person is asked to sign in again first."
          control={
            <ChoiceSelect
              label="Sign-in for remote control"
              className="w-56 max-w-full"
              value={minutes}
              options={options}
              isDisabled={!org.data}
              onChange={(v) => {
                setError(undefined);
                void routes.setStepUpMinutes(Number(v)).then(org.reload, (e) => setError(problemText(e)));
              }}
            />
          }
        />
      </ListGroup>
      <ErrorNote error={error} />
    </>
  );
}
